import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Upload, message } from 'antd';
import { proxyImageUrl, textStyles, upload } from '../../api';

function TextStyleManage() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  const categoryOptions = useMemo(() => categories.map((item) => ({ label: item.name, value: item.id })), [categories]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoryRes, itemRes] = await Promise.all([
        textStyles.categories.list(),
        textStyles.list({ include_disabled: 1 })
      ]);
      setCategories(categoryRes.data.data || []);
      setItems(itemRes.data.data || []);
    } catch (err) {
      message.error('获取文字素材失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddCategory = () => {
    setEditingCategory(null);
    categoryForm.resetFields();
    categoryForm.setFieldsValue({ sort_order: 0 });
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (record) => {
    setEditingCategory(record);
    categoryForm.setFieldsValue(record);
    setCategoryModalVisible(true);
  };

  const handleSubmitCategory = async () => {
    try {
      const values = await categoryForm.validateFields();
      if (editingCategory) {
        await textStyles.categories.update(editingCategory.id, values);
      } else {
        await textStyles.categories.create(values);
      }
      message.success('保存成功');
      setCategoryModalVisible(false);
      fetchData();
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err.response?.data?.message || '保存失败');
      }
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await textStyles.categories.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const handleAddItem = () => {
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({ sort_order: 0, enabled: true, type: 'textbox' });
    setItemModalVisible(true);
  };

  const handleEditItem = (record) => {
    setEditingItem(record);
    itemForm.setFieldsValue({
      ...record,
      json_text: JSON.stringify(record.json || {}, null, 2)
    });
    setItemModalVisible(true);
  };

  const handleUploadPreview = async (file) => {
    try {
      const res = await upload.upload(file);
      itemForm.setFieldsValue({ preview_url: res.data.data.url });
      message.success('上传成功');
    } catch (err) {
      message.error('上传失败');
    }

    return false;
  };

  const handleSubmitItem = async () => {
    try {
      const values = await itemForm.validateFields();
      let json;

      try {
        json = JSON.parse(values.json_text);
      } catch (err) {
        message.error('JSON 格式不正确');
        return;
      }

      const payload = {
        category_id: values.category_id,
        name: values.name,
        description: values.description || '',
        preview_url: values.preview_url,
        json,
        type: values.type || json.type || 'textbox',
        sort_order: values.sort_order || 0,
        enabled: values.enabled !== false
      };

      if (editingItem) {
        await textStyles.update(editingItem.id, payload);
      } else {
        await textStyles.create(payload);
      }
      message.success('保存成功');
      setItemModalVisible(false);
      fetchData();
    } catch (err) {
      if (!err?.errorFields) {
        message.error(err.response?.data?.message || '保存失败');
      }
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await textStyles.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const categoryColumns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '分类名称', dataIndex: 'name' },
    { title: '排序', dataIndex: 'sort_order', width: 120 },
    {
      title: '操作',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditCategory(record)}>编辑</Button>
          <Popconfirm title="确定删除这个分类？" onConfirm={() => handleDeleteCategory(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const itemColumns = [
    {
      title: '预览',
      dataIndex: 'preview_url',
      width: 120,
      render: (url) => (
        <div style={{ width: 88, height: 64, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {url ? <img src={proxyImageUrl(url, { width: 160, height: 120 })} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : null}
        </div>
      )
    },
    { title: '名称', dataIndex: 'name' },
    { title: '分类', dataIndex: 'category_name', width: 120 },
    { title: '类型', dataIndex: 'type', width: 100 },
    { title: '排序', dataIndex: 'sort_order', width: 90 },
    {
      title: '状态',
      dataIndex: 'enabled',
      width: 90,
      render: (enabled) => <Tag color={enabled === false ? 'default' : 'success'}>{enabled === false ? '停用' : '启用'}</Tag>
    },
    {
      title: '操作',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEditItem(record)}>编辑</Button>
          <Popconfirm title="确定删除这个文字素材？" onConfirm={() => handleDeleteItem(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Tabs
        items={[
          {
            key: 'items',
            label: '文字素材',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card>
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>文字素材管理</div>
                    <div style={{ color: '#667085' }}>管理编辑器左侧“文字”面板里的样式素材。</div>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem} style={{ width: 160 }}>添加文字素材</Button>
                  </Space>
                </Card>
                <Table rowKey="id" loading={loading} columns={itemColumns} dataSource={items} pagination={{ pageSize: 20 }} />
              </Space>
            )
          },
          {
            key: 'categories',
            label: '分类管理',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>添加分类</Button>
                <Table rowKey="id" loading={loading} columns={categoryColumns} dataSource={categories} pagination={false} />
              </Space>
            )
          }
        ]}
      />

      <Modal title={editingCategory ? '编辑分类' : '添加分类'} open={categoryModalVisible} onOk={handleSubmitCategory} onCancel={() => setCategoryModalVisible(false)} destroyOnClose>
        <Form form={categoryForm} layout="vertical">
          <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
            <Input placeholder="例如：基础" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值">
            <InputNumber style={{ width: '100%' }} placeholder="数值越大越靠前" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={editingItem ? '编辑文字素材' : '添加文字素材'} open={itemModalVisible} onOk={handleSubmitItem} onCancel={() => setItemModalVisible(false)} width={820} destroyOnClose>
        <Form form={itemForm} layout="vertical">
          <Form.Item name="name" label="素材名称" rules={[{ required: true, message: '请输入素材名称' }]}>
            <Input placeholder="例如：黄色标题" />
          </Form.Item>
          <Form.Item name="category_id" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
            <Select options={categoryOptions} placeholder="请选择分类" />
          </Form.Item>
          <Form.Item name="type" label="类型">
            <Input placeholder="textbox / group / image" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序值">
            <InputNumber style={{ width: '100%' }} placeholder="数值越大越靠前" />
          </Form.Item>
          <Form.Item name="enabled" label="前台显示" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="preview_url" label="预览图" rules={[{ required: true, message: '请上传预览图' }]}>
            <Input
              placeholder="https://img2.zhilengwang.cn/..."
              addonBefore={
                <Upload showUploadList={false} beforeUpload={handleUploadPreview} accept="image/*">
                  <Button icon={<UploadOutlined />}>上传</Button>
                </Upload>
              }
            />
          </Form.Item>
          <Form.Item name="json_text" label="Fabric JSON" rules={[{ required: true, message: '请输入 Fabric JSON' }]}>
            <Input.TextArea rows={12} placeholder='{"type":"textbox","text":"示例"}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TextStyleManage;
